import { o } from "odata"

interface ODataQuery {
  $select?: string
  $expand?: string
  $filter?: string
  $orderby?: string
  $top?: number
  $skip?: number
  $count?: boolean
  $format?: "json" | "xml"
}

/**
 * Service de base pour tous les services OData
 * Centralise la configuration et les méthodes communes
 */
export abstract class BaseService {
  protected readonly odata: ReturnType<typeof o>

  constructor() {
    const baseUrl = import.meta.env.VITE_API_ODATA_URL
    const apiKey = import.meta.env.VITE_API_ODATA_APIKEY

    if (!baseUrl || !apiKey) {
      throw new Error(
        "Configuration API manquante: VITE_API_ODATA_URL ou VITE_API_ODATA_APIKEY",
      )
    }

    this.odata = o(baseUrl, {
      headers: {
        apiKey: apiKey,
      },
    })
  }

  /**
   * Sécurise les filtres OData pour éviter les injections
   */
  protected escapeODataValue(value: string): string {
    return value.replace(/'/g, "''")
  }

  /**
   * Construit un filtre OData sécurisé pour égalité
   */
  protected createEqualFilter(field: string, value: string): string {
    return `${field} eq '${this.escapeODataValue(value)}'`
  }

  /**
   * Construit un filtre OData sécurisé pour inclusion dans une liste
   */
  protected createInFilter(field: string, values: (string | number)[]): string {
    if (values.length === 0) return "1 eq 0" // filtre toujours faux

    const escapedValues = values
      .map((v) =>
        typeof v === "string" ? `'${this.escapeODataValue(v)}'` : v.toString(),
      )
      .join(",")

    return `${field} in(${escapedValues})`
  }

  /**
   * Exécute une requête avec gestion d'erreur standard
   */
  protected async executeQuery<T>(
    entity: string,
    query: ODataQuery,
    errorContext?: string,
  ): Promise<T> {
    try {
      const results = await this.odata.get(entity).query(query)
      return results as T
    } catch (error) {
      const context = errorContext ? ` (${errorContext})` : ""
      console.error(`Erreur lors de la requête ${entity}${context}:`, error)
      throw new Error(
        `Erreur de récupération des données${context}: ${error instanceof Error ? error.message : "Erreur inconnue"}`,
      )
    }
  }
}
